/**
 * QaCaseEditor.tsx
 *
 * Self-contained create/edit editor for a single QA case (patients_qa row).
 * Renders its own header (title + optional expand button + close), tab strip
 * (Details / Q/A Notes / Assignees / Followers), scrollable body and footer.
 * Fills its parent height, so it can live inside either a right-side panel
 * (QaCasePanel) or a large fixed-height modal (QaCaseEditModal).
 *
 * Create flow: the Notes/Assignees/Followers tabs run in "draft" mode and
 * buffer their input locally; on Save the case is created first, then the
 * initial note, assignees and followers are flushed against the new case id.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { DatePicker } from '@/components/ui/DatePicker';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { formatDate as formatDisplayDate } from '@/lib/utils';
import { buildStaffOptions, staffQueryKey, toIncludeIds } from '@/lib/staffOptions';
import * as qaSvc from '@/services/qa.service';
import type { QaStatus } from '@/services/qa.service';
import * as adminSvc from '@/services/admin.service';
import { searchPatients, getPatient } from '@/services/patients.service';
import type { PatientSummary } from '@/types/patient.types';
import { listDoctors, formatDoctorName } from '@/services/doctors.service';
import { getPlansByPatient } from '@/services/plans.service';
import { QaNotesTab, isEmptyHtml } from './QaNotesTab';
import { QaFollowersTab } from './QaFollowersTab';
import type { QaFollowerDraftItem } from './QaFollowersTab';
import { Maximize2, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface QaCaseEditorProps {
  /** Existing case id → edit mode. null → create mode. */
  caseId: number | null;
  /** When opening in create mode from the patient tab, locks the Patient field. */
  patientId?: number;
  onClose: () => void;
  onSaved?: (id: number) => void;
  /** When provided, renders an expand button (e.g. panel → large modal). */
  onExpand?: () => void;
}

const STATUS_OPTIONS: Array<{ value: QaStatus; label: string }> = [
  { value: 'notInProcess',    label: 'Not in process' },
  { value: 'inProcess',       label: 'In process' },
  { value: 'unableToResolve', label: 'Unable to resolve' },
  { value: 'resolved',        label: 'Resolved' },
];

/** Legacy active-reason filter: `endDate` is either NULL or the zero-date sentinel. */
function isActiveReason(endDate: string | null | undefined): boolean {
  if (!endDate) return true;
  return endDate.startsWith('0000');
}

type TabKey = 'details' | 'notes' | 'assignees' | 'followers';

export function QaCaseEditor({ caseId, patientId, onClose, onSaved, onExpand }: QaCaseEditorProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canChangeStatus = hasPermission(25);
  const isEdit = caseId !== null;

  const [qaStatus, setQaStatus]       = useState<QaStatus>('notInProcess');
  const [doctorId, setDoctorId]       = useState<number | null>(null);
  const [procDate, setProcDate]       = useState<string>('');
  const [qaReasonIds, setQaReasonIds] = useState<number[]>([]);
  const [planId, setPlanId]           = useState<number | null>(null);
  const [facilityId, setFacilityId]   = useState<number | null>(null);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [tab, setTab]                 = useState<TabKey>('details');

  // Create-mode admin patient picker state (ignored when `patientId` is passed).
  const [selectedPatientId, setSelectedPatientId]     = useState<number | null>(null);
  const [selectedPatientName, setSelectedPatientName] = useState<string>('');

  // Create-mode draft buffers (flushed after the case is created).
  const [draftNote, setDraftNote]           = useState('');
  const [draftNoteFiles, setDraftNoteFiles] = useState<File[]>([]);
  const [draftAssignees, setDraftAssignees] = useState<QaFollowerDraftItem[]>([]);
  const [draftFollowers, setDraftFollowers] = useState<QaFollowerDraftItem[]>([]);

  const caseQuery = useQuery({
    queryKey: ['qa-case', caseId],
    queryFn: () => qaSvc.getQaItem(caseId as number),
    enabled: isEdit,
  });

  const reasonsQuery = useQuery({
    queryKey: ['qa-reasons'],
    queryFn: () => adminSvc.listQaReasons(),
  });

  // Keyed on the SAVED physician, not the form state — otherwise every pick in the
  // select would refetch the list. A saved physician who has since been
  // de-activated stays in this case's own list so the field is not left blank.
  const doctorInclude = toIncludeIds(caseQuery.data?.doctorID);

  const doctorsQuery = useQuery({
    queryKey: staffQueryKey('doctors', doctorInclude),
    queryFn: () => listDoctors({ active: true, includeIds: doctorInclude }),
  });

  const effectivePatientIdForPlans = isEdit
    ? (caseQuery.data?.patientID ?? null)
    : (patientId ?? selectedPatientId ?? null);

  const plansQuery = useQuery({
    queryKey: ['qa-patient-plans', effectivePatientIdForPlans],
    queryFn: () => getPlansByPatient(effectivePatientIdForPlans as number),
    enabled: !!effectivePatientIdForPlans,
  });

  const facilitiesQuery = useQuery({
    queryKey: ['admin-facilities'],
    queryFn: adminSvc.listFacilities,
    staleTime: 5 * 60_000,
  });

  const patientQuery = useQuery({
    queryKey: ['patient', effectivePatientIdForPlans],
    queryFn: () => getPatient(effectivePatientIdForPlans as number),
    enabled: !isEdit && !!effectivePatientIdForPlans,
  });

  // Hydrate form state from the loaded case (edit) or reset (create).
  useEffect(() => {
    if (isEdit && caseQuery.data) {
      const row = caseQuery.data;
      setQaStatus((row.qaStatus as QaStatus) ?? 'notInProcess');
      setDoctorId(row.doctorID ?? null);
      setProcDate(row.procDate && !row.procDate.startsWith('0000-00-00') ? row.procDate.slice(0, 10) : '');
      setQaReasonIds(row.qaReasonIds ?? row.reasons?.map((r) => r.qaReasonID) ?? []);
      setPlanId(row.planID ?? null);
      setFacilityId(row.facilityID ?? null);
      setError(null);
    } else if (!isEdit) {
      setQaStatus('notInProcess');
      setDoctorId(null);
      setProcDate('');
      setQaReasonIds([]);
      setPlanId(null);
      setFacilityId(null);
      setSelectedPatientId(null);
      setSelectedPatientName('');
      setDraftNote('');
      setDraftNoteFiles([]);
      setDraftAssignees([]);
      setDraftFollowers([]);
      setError(null);
    }
  }, [isEdit, caseQuery.data]);

  // Default facility to the patient's home facility when creating a new case.
  useEffect(() => {
    if (!isEdit && patientQuery.data?.facilityID) {
      setFacilityId(patientQuery.data.facilityID);
    }
  }, [isEdit, patientQuery.data?.facilityID]);

  const activeReasons = useMemo(
    () => (reasonsQuery.data ?? []).filter((r) => isActiveReason(r.endDate)),
    [reasonsQuery.data],
  );

  const doctorOptions = useMemo(
    () => buildStaffOptions(
      doctorsQuery.data ?? [],
      (d) => d.doctorID,
      (d) => formatDoctorName(d) || `#${d.doctorID}`,
    ),
    [doctorsQuery.data],
  );

  const planOptions = useMemo(
    () => (plansQuery.data ?? []).map((p) => {
      const planType = p.planTypeName?.trim() || 'No plan type';
      const facility = p.facilityName?.trim() || 'no facility';
      return { value: p.planId, label: `${planType} - ${facility}` };
    }),
    [plansQuery.data],
  );

  const facilityOptions = useMemo(
    () => (facilitiesQuery.data ?? [])
      .filter((f) => f.active)
      .map((f) => ({ value: f.facilityId, label: f.facilityName || 'Unknown' })),
    [facilitiesQuery.data],
  );

  function toggleReason(id: number, checked: boolean) {
    setQaReasonIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  }

  async function flushCreateExtras(savedId: number) {
    // Initial note (+ attachments).
    if (!isEmptyHtml(draftNote) || draftNoteFiles.length > 0) {
      try {
        const { patientsQaNoteId } = await qaSvc.createQaNote(savedId, draftNote);
        for (const f of draftNoteFiles) {
          await qaSvc.uploadQaNoteAttachment(savedId, patientsQaNoteId, f);
        }
      } catch {
        toast.error('Case created, but the initial note could not be saved.');
      }
    }
    // Assignees, then followers.
    try {
      for (const a of draftAssignees) {
        await qaSvc.addQaFollower(savedId, { followerType: a.followerType, followerId: a.followerId, isAssignee: true });
      }
      for (const f of draftFollowers) {
        await qaSvc.addQaFollower(savedId, { followerType: f.followerType, followerId: f.followerId, isAssignee: false });
      }
    } catch {
      toast.error('Case created, but some assignees/followers could not be saved.');
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        qaStatus,
        doctorID: doctorId,
        procDate: procDate || null,
        qaReasonIds,
        planId,
        facilityId,
      };

      let savedId: number;
      if (isEdit) {
        await qaSvc.updateQaCase(caseId as number, payload);
        savedId = caseId as number;
      } else {
        const effectivePatientId = patientId ?? selectedPatientId ?? undefined;
        if (!effectivePatientId) {
          setError('A patient is required to create a QA case.');
          setSaving(false);
          return;
        }
        const { doctorID, ...createPayload } = payload;
        const res = await qaSvc.createQaCase({
          patientId: effectivePatientId,
          doctorId: doctorID ?? null,
          ...createPayload,
        });
        savedId = res.patientsQaId;
        await flushCreateExtras(savedId);
      }

      queryClient.invalidateQueries({ queryKey: ['qa-cases'] });
      queryClient.invalidateQueries({ queryKey: ['qa-cases-count'] });
      if (patientId) queryClient.invalidateQueries({ queryKey: ['patient-qa', patientId] });
      if (isEdit) queryClient.invalidateQueries({ queryKey: ['qa-case', caseId] });

      toast.success(isEdit ? 'QA case saved.' : 'QA case created.');
      onSaved?.(savedId);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save QA case.');
    } finally {
      setSaving(false);
    }
  }

  const caseRow = caseQuery.data;
  const loading = isEdit && caseQuery.isLoading;
  const title = isEdit
    ? (caseRow?.patientName?.trim() ? `Q/A — ${caseRow.patientName.trim()}` : 'Edit Q/A Case')
    : 'New Q/A Case';

  const TABS: Array<{ id: TabKey; label: string }> = [
    { id: 'details', label: 'Details' },
    { id: 'notes', label: 'Q/A Notes' },
    { id: 'assignees', label: 'Assignees' },
    { id: 'followers', label: 'Followers' },
  ];

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
        <h2 className="truncate text-base font-semibold text-slate-800">{title}</h2>
        <div className="flex items-center gap-1">
          {onExpand && (
            <button
              type="button"
              onClick={onExpand}
              title="Expand"
              className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            title="Close"
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex shrink-0 gap-1 border-b border-slate-200 px-4">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={
              'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ' +
              (tab === t.id
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700')
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Body */}
      {/* Extra bottom padding below `md` so the last Reason checkbox clears the shrink-0 footer
          once the sheet is only as tall as the visible viewport. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 max-md:pb-8">
        {loading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : (
          <>
            {tab === 'notes' && (
              <QaNotesTab
                caseId={caseId}
                draft={isEdit ? undefined : { note: draftNote, setNote: setDraftNote, files: draftNoteFiles, setFiles: setDraftNoteFiles }}
              />
            )}
            {tab === 'assignees' && (
              <QaFollowersTab
                caseId={caseId}
                mode="assignees"
                draft={isEdit ? undefined : { items: draftAssignees, setItems: setDraftAssignees }}
              />
            )}
            {tab === 'followers' && (
              <QaFollowersTab
                caseId={caseId}
                mode="followers"
                draft={isEdit ? undefined : { items: draftFollowers, setItems: setDraftFollowers }}
              />
            )}

            {tab === 'details' && (
              <div className="space-y-5">
                {error && (
                  <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
                )}

                {isEdit && caseRow && (
                  /* Not in the ticket — this strip only renders in edit mode and the report is
                     about the create modal. Stacked anyway: it is the same unprefixed two-up in
                     the same modal, and a long patient name in a ~150px column wraps badly.
                     Leaving one of four unstacked is how the next pass finds it. */
                  <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                    <div>
                      <span className="block text-xs font-medium text-slate-500">Patient</span>
                      <span className="text-slate-800">{caseRow.patientName?.trim() || '—'}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-slate-500">Case ID</span>
                      <span className="text-slate-800">#{caseRow.patientsQaID}</span>
                    </div>
                  </div>
                )}

                {!isEdit && patientId === undefined && (
                  <FormField label="Patient" required>
                    <PatientPicker
                      value={selectedPatientId}
                      displayValue={selectedPatientName}
                      onSelect={(id, name) => { setSelectedPatientId(id); setSelectedPatientName(name); }}
                    />
                  </FormField>
                )}

                {!isEdit && patientId !== undefined && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                    <span className="block text-xs font-medium text-slate-500">Patient</span>
                    <span className="text-slate-800">Patient #{patientId}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
                  <FormField label="Status" required>
                    <Select<QaStatus>
                      value={qaStatus}
                      onChange={(v) => setQaStatus(v ?? 'notInProcess')}
                      options={STATUS_OPTIONS}
                      disabled={!canChangeStatus}
                    />
                  </FormField>
                  <FormField label="Medical Provider">
                    <Select<number>
                      value={doctorId}
                      onChange={(v) => setDoctorId(v)}
                      options={doctorOptions}
                      placeholder="Select one..."
                      searchable
                      clearable
                      loading={doctorsQuery.isLoading}
                    />
                  </FormField>
                </div>

                <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
                  <FormField label="Plan">
                    <Select<number>
                      value={planId}
                      onChange={(v) => setPlanId(v)}
                      options={planOptions}
                      placeholder={effectivePatientIdForPlans ? 'Select a plan...' : 'Select a patient first'}
                      searchable
                      clearable
                      loading={plansQuery.isLoading}
                      disabled={!effectivePatientIdForPlans}
                    />
                  </FormField>
                  <FormField label="Facility">
                    <Select<number>
                      value={facilityId}
                      onChange={(v) => setFacilityId(v)}
                      options={facilityOptions}
                      placeholder="Select a facility..."
                      searchable
                      clearable
                      loading={facilitiesQuery.isLoading}
                    />
                  </FormField>
                </div>

                <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
                  {/* SCRUM-1441. This was a raw `<input type="date">` with a bespoke class —
                      the only field in this form not using a shared component, which is why it
                      read as grey and disabled beside the white Selects above, carried no
                      placeholder and no calendar icon, and did not line up with the rows above
                      it: a native date control's min-content width is UA-determined and wider
                      than a styled Select, so both columns overran their tracks and each other.
                      DatePicker also picks up index.css's `max-md:appearance-none` and the
                      shadow-part left-align, neither of which a bespoke class can reach. */}
                  <FormField label="Date of Procedure" htmlFor="qa-proc-date">
                    <DatePicker
                      id="qa-proc-date"
                      value={procDate}
                      onChange={setProcDate}
                      clearable
                      placeholder="No date"
                    />
                  </FormField>
                  {/* "Will be set on create" was rendered as the field's VALUE, inside a box styled
                      to look like an input. FormField has had `helpText` all along. In edit mode
                      there is a real value, so the read-only box stays. */}
                  {isEdit ? (
                    <FormField label="Date Added">
                      <div className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                        {formatDisplayDate(caseRow?.addDate)}
                      </div>
                    </FormField>
                  ) : (
                    <FormField
                      label="Date Added"
                      helpText="Set automatically when the case is created"
                    >
                      <div className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-400">
                        —
                      </div>
                    </FormField>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Reason</label>
                  {reasonsQuery.isLoading ? (
                    <div className="flex justify-center py-4"><Spinner /></div>
                  ) : activeReasons.length === 0 ? (
                    <p className="text-sm italic text-slate-500">No active QA reasons configured.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg border border-slate-200 bg-white px-3 py-3 md:grid-cols-3">
                      {activeReasons.map((r) => (
                        <Checkbox
                          key={r.qaReasonId}
                          label={r.title}
                          checked={qaReasonIds.includes(r.qaReasonId)}
                          onChange={(e) => toggleReason(r.qaReasonId, e.target.checked)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 px-4 py-3">
        <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving || loading} loading={saving}>Save</Button>
      </div>
    </div>
  );
}

export default QaCaseEditor;

// ─── PatientPicker ───────────────────────────────────────────────────────────
// Minimal debounced search dropdown for the admin "New Q/A Case" flow.

interface PatientPickerProps {
  value: number | null;
  displayValue: string;
  onSelect: (patientId: number | null, displayName: string) => void;
}

function PatientPicker({ value, displayValue, onSelect }: PatientPickerProps) {
  const [text, setText]       = useState(displayValue);
  const [results, setResults] = useState<PatientSummary[]>([]);
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setText(displayValue); }, [displayValue]);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setText(next);
    if (!next.trim()) {
      if (value !== null) onSelect(null, '');
      setResults([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (next.trim().length < 2) return;
      setLoading(true);
      try {
        const patients = await searchPatients(next.trim());
        setResults(patients);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  }

  function handleSelect(p: PatientSummary) {
    const name = `${p.firstName} ${p.lastName}`.trim();
    setText(name);
    onSelect(Number(p.patientId), name);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={text}
        onChange={handleChange}
        onFocus={() => { if (results.length > 0 && text) setOpen(true); }}
        placeholder="Search patients by name..."
      />
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500" />
        </div>
      )}
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {results.map((p) => (
            <button
              key={p.patientId}
              type="button"
              onClick={() => handleSelect(p)}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
            >
              <span className="font-medium text-slate-900">{p.firstName} {p.lastName}</span>
              <span className="ml-2 text-xs text-slate-400">ID: {p.patientId}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
